import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Link, useLocation, useNavigationType } from 'react-router-dom';
import { useTranslate } from '../components/TranslationText';
import ActionButton from '../components/ActionButton';
import { useSelector } from 'react-redux';
import axios from "../axios/axiosUmbraco";
import NewsFilter from '../components/NewsFilter';
import FilterButton from '../components/FilterButton';
import NewsItemHome from '../components/NewsItemHome';
import { formatDateTimeToString } from '../helper/formatDateTimeToString';
import { filterResponsive, pageTitle, rightResponsive } from '../common/cssclasses';
import { updateHeadTags } from '../helper/updateHeadTags';
import useUmbracoQuery from '../hooks/useUmbracoQuery';
import { formatUmbracoDate } from '../helper/formatUmbracoDate';
import {
    checkIsReturning,
    setReturnTicket,
    consumeReturnTicket
} from '../helper/pageFilterPersistence';

const fetchId = process.env.REACT_APP_HOME_PAGE_LATSEST_NEWS_ID;
const NEWS_TICKET_KEY = "news_return_from_details";
const NEWS_RAW_FILTERS_KEY = "news_raw_filters";
const NEWS_API_FILTERS_KEY = "news_api_filters";

function clearNewsSessionStorage() {
    sessionStorage.removeItem(NEWS_RAW_FILTERS_KEY);
    sessionStorage.removeItem(NEWS_API_FILTERS_KEY);
    sessionStorage.removeItem(NEWS_TICKET_KEY);
}

export default function NewsPage() {
    const { translate } = useTranslate();
    const { currentLanguage } = useSelector((state) => state.studBookSlice) ?? 'en';
    const itemsPerPage = 12;

    const navigationType = useNavigationType();
    const location = useLocation();
    const prevLocationKey = useRef(location.key);
    const isInitialMount = useRef(true);
    const wasReturningRef = useRef(false);

    if (isInitialMount.current) {
        isInitialMount.current = false;
        const isReturning = checkIsReturning(NEWS_TICKET_KEY, navigationType, "news");
        wasReturningRef.current = isReturning;
        if (!isReturning) {
            clearNewsSessionStorage();
        }
    }
    
    const [showFilter, setShowFilter] = useState(false);
    const [rawFilters, setRawFilters] = useState(() => {
        const saved = sessionStorage.getItem(NEWS_RAW_FILTERS_KEY);
        return saved ? JSON.parse(saved) : {};
    });
    const [apiFilters, setApiFilters] = useState(() => {
        const saved = sessionStorage.getItem(NEWS_API_FILTERS_KEY);
        return saved ? JSON.parse(saved) : {};
    });
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);

    useEffect(() => {
        sessionStorage.setItem(NEWS_RAW_FILTERS_KEY, JSON.stringify(rawFilters));
    }, [rawFilters]);

    useEffect(() => {
        sessionStorage.setItem(NEWS_API_FILTERS_KEY, JSON.stringify(apiFilters));
    }, [apiFilters]);

    useEffect(() => {
        if (wasReturningRef.current) {
            consumeReturnTicket(NEWS_TICKET_KEY);
        }
    }, []);

    useEffect(() => {
        if (prevLocationKey.current !== location.key) {
            prevLocationKey.current = location.key;
            if (navigationType === "PUSH") {
                clearNewsSessionStorage();
                setRawFilters({});
                setApiFilters({});
            }
        }
    }, [location.key, navigationType]);

    const {
        data: latestNewsRaw,
        isLoading,
        hasMore,
        fetchNextPage
    } = useUmbracoQuery(fetchId, apiFilters, itemsPerPage);

    useEffect(() => {
        const handleResize = () => {
            setWindowWidth(window.innerWidth);
        };
        window.addEventListener("resize", handleResize);
        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    function getCurrentLanguageUmbraco() {
        const lang = localStorage.getItem("eahsLang") ?? "en";
        return lang === "en" ? 'en-US' : 'ar-AE';
    }

    // Fetch SEO data independently
    useEffect(() => {
        async function fetchSEO() {
            try {
                const seoId = fetchId.replace('descendants:', '');
                const seo = await axios.get(`/umbraco/delivery/api/v1/content/item/?id=${seoId}`, {
                    headers: {
                        "accept-language": getCurrentLanguageUmbraco(),
                    },
                });
                if (seo?.data?.length > 0) {
                    const seoData = seo.data[0].properties;
                    updateHeadTags({
                        keywords: seoData?.seoKeywords,
                        description: seoData?.seoDescription,
                        title: seoData?.seoTitle,
                    })
                }
            } catch (e) {
                console.error("SEO fetch error", e);
            }
        }
        fetchSEO();
    }, [currentLanguage]);

    const formattedData = useMemo(() => {
        return latestNewsRaw.map((item) => ({
            id: item.id,
            name: item.name,
            Item_Body: item?.properties?.Item_Body?.markup ?? '',
            Item_CoverImage: item.properties?.Item_CoverImage ? item.properties?.Item_CoverImage[0]?.url : '',
            Item_Date: item.properties.Item_Date ? formatDateTimeToString(item?.properties?.Item_Date, currentLanguage === 'en' ? 'en-US' : 'ar-AE') : '',
            Item_ShortDescription: item?.properties?.Item_ShortDescription ?? '',
            Item_Title: item?.properties?.Item_Title ?? '',
            createdBy: item?.properties?.createdBy ?? '',
            Item_Date_Normal: item.properties.Item_Date ? new Date(item?.properties?.Item_Date) : '',
            externalLink: item?.properties?.externalLink ?? false,
            item_Feature: item?.properties?.item_Feature ?? true,
            newsUrl: item?.properties?.externalLink ? item?.properties?.newsUrl : `/singlePost?id=${item.id}&title=${encodeURIComponent(item?.properties?.Item_Title || item.name)}`,
            topOne: item?.properties?.topOne ?? false,
        }));
    }, [latestNewsRaw, currentLanguage]);

    const onSubmit = (filters) => {
        setRawFilters(filters);
        const newApiFilters = {};

        // Flutter API key: newsTitle
        newApiFilters['newsTitle'] = filters.newsName?.trim() || '';

        // Flutter API key: newsCategory — values: "From EAHS", "About EAHS", "" (all)
        newApiFilters['newsCategory'] = filters.newscategory || '';

        // Flutter API key: newsClassification — values: "General", "Activities and events", "" (all)
        newApiFilters['newsClassification'] = filters.newsClassification || '';

        // Flutter API keys: newsDateFrom, newsDateTo — format: yyyy-MM-dd
        newApiFilters['newsDateFrom'] = filters.DateFrom ? formatUmbracoDate(filters.DateFrom) : '';
        newApiFilters['newsDateTo'] = filters.DateTo ? formatUmbracoDate(filters.DateTo) : '';

        setApiFilters(newApiFilters);
    };

    return (
        <>
            <div className="row rtl">
                {windowWidth < 768 && (
                    <div className="col-md-4 col-sm-6 col-6 text-center d-flex justify-content-center mb-3 mx-auto">
                        <FilterButton onClick={() => setShowFilter(!showFilter)} />
                    </div>
                )}
                
                <h2 className={pageTitle}>
                    {translate('web.latestNews', 'Latest News')}
                </h2>
                
                <div
                    className={filterResponsive}
                    style={{
                        display: windowWidth >= 768 ? 'block' : showFilter ? 'block' : 'none',
                    }}
                >
                    <NewsFilter onSubmit={onSubmit} currentFilters={rawFilters} />
                </div>
                
                <div className={rightResponsive}>
                    {isLoading && formattedData.length === 0 && (
                        <div className="text-center w-100 mt-5">
                            <i className="fas fa-spinner fa-spin fa-3x color-eahsred"></i>
                        </div>
                    )}
                    
                    {!isLoading && formattedData.length === 0 && (
                        <div className="text-center w-100 mt-5 bold color-eahsred">
                            {translate('web.nodataFound', 'No News Found with Your Filter Parameters')}
                        </div>
                    )}

                    <div className="row rtl">
                        <div className="row m0 rtl">
                            {formattedData.length > 0 && formattedData.map((item, index) => (
                                <Link
                                    to={item.newsUrl}
                                    target={item.externalLink ? '_blank' : '_self'}
                                    onClick={() => {
                                        if (!item.externalLink) {
                                            setReturnTicket(NEWS_TICKET_KEY);
                                        }
                                    }}
                                    className='col-xl-4 col-lg-6 col-md-12 px-5px col-sm-12 justRotate mb-main d-block'
                                    style={{ marginBottom: '0.8rem' }}
                                    key={index}
                                >
                                    <NewsItemHome item={item} windowWidtht={windowWidth} />
                                </Link>
                            ))}
                        </div>
                        <div className='col-lg-12 col-md-12 px-5px col-sm-12 my-5 text-center'>
                            {hasMore && formattedData.length > 0 && (
                                <ActionButton label={translate('web.showMore', 'Show More')} type={'loadmore'} onClick={fetchNextPage} disabled={isLoading} />
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

